/**
 * Skeleton — animated placeholder for loading states.
 *
 * Design system mapping:
 *   • Background: bg-webex-gray-100 dark:bg-webex-gray-900
 *   • Animation:  animate-pulse (motion-reduce:animate-none)
 *   • Shape:      line=h-4 w-full rounded · box=rounded-md · circle=rounded-full
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const skeletonVariants = cva(
  "bg-webex-gray-100 dark:bg-webex-gray-900 animate-pulse motion-reduce:animate-none",
  {
    variants: {
      shape: {
        line: "h-4 w-full rounded",
        box: "rounded-md",
        circle: "rounded-full",
      },
    },
    defaultVariants: {
      shape: "line",
    },
  },
)

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {
  width?: string | number
  height?: string | number
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, shape, width, height, style, ...props }, ref) => {
    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={cn(skeletonVariants({ shape }), className)}
        style={{ width, height, ...style }}
        {...props}
      />
    )
  },
)
Skeleton.displayName = "Skeleton"
